/**
 * Looks Layer Strategy Implementation
 *
 * Implements layer-specific logic for Looks (L) layer processing.
 * Looks layer handles image files only for fashion and styling assets.
 */

import {
  LOOKS_METADATA,
  validateStringMaxLength,
  validateArrayLength,
  validateArrayItems,
} from '../../../constants/apiMetadataConstraints';
import {
  BaseLayerStrategy,
  LayerFieldConfig,
  ValidationResult,
} from './LayerStrategy';

export interface LooksLayerMetadata {
  layerType: 'looks';

  // Core look information
  lookName?: string;
  lookType?: 'Outfit' | 'Style' | 'Fashion' | 'Accessory' | 'Makeup' | 'Hair';
  description?: string;

  // Style characteristics
  fashionStyle?: string[];
  colorScheme?: string[];
  occasion?: string[];
  season?: string[];

  // Fashion metadata
  brand?: string;
  designer?: string;
  collection?: string;
  year?: number;

  // Visual characteristics
  dominantColors?: string[];
  pattern?: string;
  texture?: string;
  silhouette?: string;

  // Styling details
  accessories?: string[];
  materials?: string[];
  priceRange?: 'Budget' | 'Mid-Range' | 'Luxury' | 'High-End';

  // AI-generated analysis
  styleComplexity?: 'Simple' | 'Moderate' | 'Complex';
  versatility?: number; // 0-1 (how versatile the look is)
  trendiness?: number; // 0-1 (how trendy the look is)
  formalityLevel?: 'Casual' | 'Semi-Formal' | 'Formal' | 'Black-Tie';

  // Social metrics
  likeCount?: number;
  saveCount?: number;
  shareCount?: number;
  influencerRating?: number;
}

export class LooksLayerStrategy extends BaseLayerStrategy {
  readonly layerCode = 'L';
  readonly layerName = 'Looks';
  readonly layerDescription = 'Fashion looks, outfits, and styling assets';

  getRequiredFields(): string[] {
    // Updated to match backend API requirements (LooksMetadataRequest)
    return ['outfitName', 'styleCategory', 'style', 'occasion', 'colorScheme', 'primaryGarment', 'primaryColors', 'formality'];
  }

  getOptionalFields(): string[] {
    return [
      'description',
      'fashionStyle',
      'colorScheme',
      'occasion',
      'season',
      'brand',
      'designer',
      'collection',
      'year',
      'dominantColors',
      'pattern',
      'texture',
      'silhouette',
      'accessories',
      'materials',
      'priceRange',
      'styleComplexity',
      'versatility',
      'trendiness',
      'formalityLevel',
      'likeCount',
      'saveCount',
      'shareCount',
      'influencerRating',
    ];
  }

  getFieldConfig(): LayerFieldConfig {
    return {
      required: this.getRequiredFields(),
      optional: this.getOptionalFields(),
      computed: [
        'dominantColors',
        'styleComplexity',
        'versatility',
        'trendiness',
      ],
      inherited: ['fashionStyle', 'colorScheme'],
    };
  }

  extractMetadataFromAI(aiResponse: any): LooksLayerMetadata {
    const layerMetadata =
      aiResponse.data?.layerMetadata ||
      aiResponse.layerMetadata ||
      aiResponse.looksMetadata ||
      {};

    return {
      layerType: 'looks',

      // Core information
      lookName:
        layerMetadata.outfitName ||
        layerMetadata.lookName ||
        aiResponse.outfitName ||
        aiResponse.lookName ||
        '',
      lookType: this.normalizeLookType(layerMetadata.style || aiResponse.style),
      description: layerMetadata.description || aiResponse.description || '',

      // Style characteristics
      fashionStyle: this.normalizeArray(
        layerMetadata.style || layerMetadata.fashionStyle || aiResponse.style
      ),
      colorScheme: this.normalizeArray(
        layerMetadata.colorScheme || aiResponse.colorScheme
      ),
      occasion: this.normalizeArray(
        layerMetadata.occasion || aiResponse.occasion
      ),
      season: this.normalizeArray(
        layerMetadata.seasonality || layerMetadata.season || aiResponse.seasonality || aiResponse.season
      ),

      // Fashion metadata
      brand: layerMetadata.brand || aiResponse.brand,
      designer: layerMetadata.designer || aiResponse.designer,
      collection: layerMetadata.collection || aiResponse.collection,
      year: layerMetadata.year || aiResponse.year,

      // Visual characteristics from AI analysis
      dominantColors: this.normalizeArray(
        layerMetadata.dominantColors || aiResponse.dominantColors
      ),
      pattern: layerMetadata.pattern || aiResponse.pattern,
      texture: layerMetadata.texture || aiResponse.texture,
      silhouette: layerMetadata.silhouette || aiResponse.silhouette,

      // Styling details
      accessories: this.normalizeArray(
        layerMetadata.accessories || aiResponse.accessories
      ),
      materials: this.normalizeArray(
        layerMetadata.materials || aiResponse.materials
      ),
      priceRange: this.normalizePriceRange(
        layerMetadata.priceRange || aiResponse.priceRange
      ),

      // AI-generated analysis
      styleComplexity: this.normalizeComplexity(
        layerMetadata.styleComplexity || aiResponse.styleComplexity
      ),
      versatility: layerMetadata.versatility || aiResponse.versatility,
      trendiness: layerMetadata.trendiness || aiResponse.trendiness,
      formalityLevel: this.normalizeFormalityLevel(
        layerMetadata.formality || layerMetadata.formalityLevel || aiResponse.formality
      ),
    };
  }

  enhanceWithDefaults(metadata: LooksLayerMetadata): LooksLayerMetadata {
    return {
      ...this.getDefaultMetadata(),
      ...metadata,
      layerType: 'looks',

      // Ensure required fields have defaults
      lookName: metadata.lookName || 'Untitled Look',
      lookType: metadata.lookType || 'Style',

      // Normalize arrays
      fashionStyle: this.normalizeArray(metadata.fashionStyle),
      colorScheme: this.normalizeArray(metadata.colorScheme),
      occasion: this.normalizeArray(metadata.occasion),
      season: this.normalizeArray(metadata.season),
      dominantColors: this.normalizeArray(metadata.dominantColors),
      accessories: this.normalizeArray(metadata.accessories),
      materials: this.normalizeArray(metadata.materials),

      // Default AI values
      styleComplexity: metadata.styleComplexity || 'Moderate',
      formalityLevel: metadata.formalityLevel || 'Casual',
    };
  }

  generateTags(metadata: LooksLayerMetadata, description?: string): string[] {
    const tags = new Set<string>();

    // Basic layer tags
    tags.add('looks');
    tags.add('fashion');
    tags.add('style');

    // Look type tags
    if (metadata.lookType) {
      tags.add(metadata.lookType.toLowerCase());
      switch (metadata.lookType) {
        case 'Outfit':
          tags.add('outfit');
          tags.add('complete-look');
          break;
        case 'Fashion':
          tags.add('fashion');
          tags.add('trendy');
          break;
        case 'Accessory':
          tags.add('accessories');
          tags.add('styling');
          break;
        case 'Makeup':
          tags.add('makeup');
          tags.add('beauty');
          break;
        case 'Hair':
          tags.add('hairstyle');
          tags.add('hair');
          break;
      }
    }

    // Fashion style tags
    if (metadata.fashionStyle && metadata.fashionStyle.length > 0) {
      metadata.fashionStyle.forEach(style => {
        tags.add(style.toLowerCase().replace(/\s+/g, '-'));
        tags.add(`${style.toLowerCase()}-style`);
      });
    }

    // Color scheme tags
    if (metadata.colorScheme && metadata.colorScheme.length > 0) {
      metadata.colorScheme.forEach(color => {
        tags.add(color.toLowerCase());
        tags.add(`${color.toLowerCase()}-color`);
      });
    }

    // Occasion tags
    if (metadata.occasion && metadata.occasion.length > 0) {
      metadata.occasion.forEach(occ => {
        tags.add(occ.toLowerCase().replace(/\s+/g, '-'));
      });
    }

    // Season tags
    if (metadata.season && metadata.season.length > 0) {
      metadata.season.forEach(s => {
        tags.add(s.toLowerCase());
        tags.add(`${s.toLowerCase()}-season`);
      });
    }

    // Brand tags
    if (metadata.brand) {
      tags.add(metadata.brand.toLowerCase().replace(/\s+/g, '-'));
      tags.add('branded');
    }

    // Designer tags
    if (metadata.designer) {
      tags.add(metadata.designer.toLowerCase().replace(/\s+/g, '-'));
      tags.add('designer');
    }

    // Price range tags
    if (metadata.priceRange) {
      tags.add(metadata.priceRange.toLowerCase().replace(/-/g, ''));
      switch (metadata.priceRange) {
        case 'Budget':
          tags.add('affordable');
          tags.add('budget-friendly');
          break;
        case 'Luxury':
        case 'High-End':
          tags.add('luxury');
          tags.add('premium');
          tags.add('high-end');
          break;
      }
    }

    // Formality tags
    if (metadata.formalityLevel) {
      tags.add(metadata.formalityLevel.toLowerCase().replace(/\s+/g, '-'));
      switch (metadata.formalityLevel) {
        case 'Casual':
          tags.add('everyday');
          tags.add('relaxed');
          break;
        case 'Formal':
        case 'Black-Tie':
          tags.add('formal');
          tags.add('elegant');
          break;
      }
    }

    // Style complexity tags
    if (metadata.styleComplexity) {
      tags.add(`${metadata.styleComplexity.toLowerCase()}-complexity`);
    }

    // Versatility tags
    if (metadata.versatility && metadata.versatility > 0.7) {
      tags.add('versatile');
      tags.add('multi-occasion');
    }

    // Trendiness tags
    if (metadata.trendiness && metadata.trendiness > 0.7) {
      tags.add('trendy');
      tags.add('on-trend');
      tags.add('fashionable');
    }

    return Array.from(tags).slice(0, 30);
  }

  categorizeTags(tags: string[]): {
    primary: string[];
    secondary: string[];
    descriptive: string[];
  } {
    const categories = {
      primary: [] as string[],
      secondary: [] as string[],
      descriptive: [] as string[],
    };

    tags.forEach(tag => {
      const lowerTag = tag.toLowerCase();

      // Primary tags - type and main style
      if (
        lowerTag.includes('fashion') ||
        lowerTag.includes('style') ||
        lowerTag.includes('look') ||
        ['outfit', 'makeup', 'accessory', 'hair'].includes(lowerTag)
      ) {
        categories.primary.push(tag);
      }
      // Secondary tags - characteristics and occasions
      else if (
        lowerTag.includes('occasion') ||
        lowerTag.includes('season') ||
        lowerTag.includes('formal') ||
        lowerTag.includes('casual')
      ) {
        categories.secondary.push(tag);
      }
      // Descriptive tags - colors, brands, specifics
      else {
        categories.descriptive.push(tag);
      }
    });

    return categories;
  }

  validateBusinessRules(metadata: LooksLayerMetadata): ValidationResult {
    const errors: any[] = [];
    const warnings: any[] = [];

    // OpenAPI LooksMetadataRequest: string maxLength 255, primaryColors min 1 max 10, others max 10
    const outfitName = (metadata as any).outfitName || metadata.lookName;
    if (outfitName) {
      const msg = validateStringMaxLength(outfitName, LOOKS_METADATA.stringMaxLength, 'Outfit name');
      if (msg) errors.push({ field: 'outfitName', message: msg, severity: 'error' });
    }
    const primaryColors = (metadata as any).primaryColors;
    if (primaryColors && Array.isArray(primaryColors)) {
      const msg = validateArrayLength(primaryColors, LOOKS_METADATA.primaryColors, 'Primary colors');
      if (msg) errors.push({ field: 'primaryColors', message: msg, severity: 'error' });
      else {
        const itemMsg = validateArrayItems(primaryColors, LOOKS_METADATA.stringMaxLength, 'Primary colors');
        if (itemMsg) errors.push({ field: 'primaryColors', message: itemMsg, severity: 'error' });
      }
    }
    for (const [key, opts] of [
      ['patterns', LOOKS_METADATA.patterns],
      ['materials', LOOKS_METADATA.materials],
      ['accessories', LOOKS_METADATA.accessories],
      ['brandNames', LOOKS_METADATA.brandNames],
    ] as const) {
      const arr = (metadata as any)[key];
      if (arr && Array.isArray(arr)) {
        const msg = validateArrayLength(arr, opts, key);
        if (msg) errors.push({ field: key, message: msg, severity: 'error' });
        else {
          const itemMsg = validateArrayItems(arr, LOOKS_METADATA.stringMaxLength, key);
          if (itemMsg) errors.push({ field: key, message: itemMsg, severity: 'error' });
        }
      }
    }

    // Business rule: Look name should be meaningful
    if (metadata.lookName && metadata.lookName.trim().length < 3) {
      warnings.push({
        field: 'lookName',
        message: 'Look name should be at least 3 characters long',
        suggestion: 'Provide a more descriptive look name',
      });
    }

    // Business rule: Should have at least one style characteristic
    if (
      (!metadata.fashionStyle || metadata.fashionStyle.length === 0) &&
      (!metadata.colorScheme || metadata.colorScheme.length === 0) &&
      (!metadata.occasion || metadata.occasion.length === 0)
    ) {
      warnings.push({
        field: 'fashionStyle',
        message:
          'Look should have at least one style characteristic (fashion style, color scheme, or occasion)',
        suggestion: 'Add fashion style, color scheme, or occasion tags',
      });
    }

    // Business rule: Year should be valid if provided
    const currentYear = new Date().getFullYear();
    if (
      metadata.year &&
      (metadata.year < 1900 || metadata.year > currentYear + 1)
    ) {
      warnings.push({
        field: 'year',
        message: `Year should be between 1900 and ${currentYear + 1}`,
        suggestion: 'Verify the collection year',
      });
    }

    // Business rule: Versatility and trendiness should be 0-1
    if (
      metadata.versatility &&
      (metadata.versatility < 0 || metadata.versatility > 1)
    ) {
      warnings.push({
        field: 'versatility',
        message: 'Versatility should be a value between 0 and 1',
        suggestion: 'Provide a decimal value between 0 and 1',
      });
    }

    if (
      metadata.trendiness &&
      (metadata.trendiness < 0 || metadata.trendiness > 1)
    ) {
      warnings.push({
        field: 'trendiness',
        message: 'Trendiness should be a value between 0 and 1',
        suggestion: 'Provide a decimal value between 0 and 1',
      });
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  getDefaultMetadata(): LooksLayerMetadata {
    return {
      layerType: 'looks',
      lookName: '',
      lookType: 'Style',
      description: '',
      fashionStyle: [],
      colorScheme: [],
      occasion: [],
      season: [],
      dominantColors: [],
      accessories: [],
      materials: [],
      styleComplexity: 'Moderate',
      formalityLevel: 'Casual',
    };
  }

  getInheritanceRules(): {
    [field: string]: 'always' | 'if_empty' | 'never' | 'merge';
  } {
    return {
      // Looks are unique - don't inherit names
      lookName: 'never',
      lookType: 'never',
      brand: 'never',
      designer: 'never',

      // Could inherit style characteristics from collections
      fashionStyle: 'merge',
      colorScheme: 'merge',
      occasion: 'merge',
      season: 'merge',
    };
  }

  private normalizeArray(value: any): string[] {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      return value
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0);
    }
    return [];
  }

  private normalizeLookType(value: any): LooksLayerMetadata['lookType'] {
    if (!value) return 'Style';

    const normalized = String(value).toLowerCase();

    if (normalized.includes('outfit')) return 'Outfit';
    if (normalized.includes('fashion')) return 'Fashion';
    if (normalized.includes('accessory')) return 'Accessory';
    if (normalized.includes('makeup') || normalized.includes('beauty'))
      return 'Makeup';
    if (normalized.includes('hair')) return 'Hair';

    return 'Style';
  }

  private normalizePriceRange(value: any): LooksLayerMetadata['priceRange'] {
    if (!value) return undefined;

    const normalized = String(value).toLowerCase();

    if (
      normalized.includes('budget') ||
      normalized.includes('cheap') ||
      normalized.includes('affordable')
    )
      return 'Budget';
    if (normalized.includes('mid') || normalized.includes('moderate'))
      return 'Mid-Range';
    if (normalized.includes('luxury') || normalized.includes('expensive'))
      return 'Luxury';
    if (normalized.includes('high') || normalized.includes('premium'))
      return 'High-End';

    return 'Mid-Range';
  }

  private normalizeComplexity(
    value: any
  ): LooksLayerMetadata['styleComplexity'] {
    if (!value) return 'Moderate';

    const normalized = String(value).toLowerCase();

    if (normalized.includes('simple') || normalized.includes('basic'))
      return 'Simple';
    if (normalized.includes('complex') || normalized.includes('elaborate'))
      return 'Complex';

    return 'Moderate';
  }

  private normalizeFormalityLevel(
    value: any
  ): LooksLayerMetadata['formalityLevel'] {
    if (!value) return 'Casual';

    const normalized = String(value).toLowerCase();

    if (normalized.includes('casual') || normalized.includes('everyday'))
      return 'Casual';
    if (normalized.includes('semi') || normalized.includes('business'))
      return 'Semi-Formal';
    if (normalized.includes('black') || normalized.includes('tie'))
      return 'Black-Tie';
    if (normalized.includes('formal') || normalized.includes('dress'))
      return 'Formal';

    return 'Casual';
  }
}

// Export singleton instance
export const looksLayerStrategy = new LooksLayerStrategy();
